import { useState, useEffect } from 'react';
import { Snowflake, Calendar } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export const DebtPayoffCalculator = () => {
  const [totalDebt, setTotalDebt] = useState(150000);
  const [interestRate, setInterestRate] = useState(15);
  const [minimumPayment, setMinimumPayment] = useState(5000);
  const [extraPayment, setExtraPayment] = useState(2000);
  
  const [monthsWithMin, setMonthsWithMin] = useState(0);
  const [monthsWithExtra, setMonthsWithExtra] = useState(0);
  const [savedInterest, setSavedInterest] = useState(0);

  useEffect(() => {
    const monthlyRate = interestRate / 100 / 12;
    
    // Without extra payment
    let balance1 = totalDebt;
    let months1 = 0;
    let totalPaid1 = 0;
    
    while (balance1 > 0 && months1 < 600) {
      const interest = balance1 * monthlyRate;
      const payment = Math.min(minimumPayment, balance1 + interest);
      balance1 = balance1 + interest - payment;
      totalPaid1 += payment;
      months1++;
    }
    
    // With extra payment
    let balance2 = totalDebt;
    let months2 = 0;
    let totalPaid2 = 0;
    const totalPayment = minimumPayment + extraPayment;
    
    while (balance2 > 0 && months2 < 600) {
      const interest = balance2 * monthlyRate;
      const payment = Math.min(totalPayment, balance2 + interest);
      balance2 = balance2 + interest - payment;
      totalPaid2 += payment;
      months2++;
    }
    
    setMonthsWithMin(months1);
    setMonthsWithExtra(months2);
    setSavedInterest(Math.round(totalPaid1 - totalPaid2));
  }, [totalDebt, interestRate, minimumPayment, extraPayment]);

  const chartData = [
    { name: 'Minimální splátky', months: monthsWithMin },
    { name: 'S extra splátkou', months: monthsWithExtra },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{data.name}</p>
          <p className="text-green font-bold">{data.months} měsíců ({(data.months / 12).toFixed(1)} let)</p>
        </div>
      );
    }
    return null;
  };

  const monthsSaved = monthsWithMin - monthsWithExtra;

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">S extra splátkou ušetříte</p>
        <p className="text-4xl font-bold text-green">{savedInterest.toLocaleString('cs-CZ')} Kč</p>
        <p className="text-gray-500 text-sm">na úrocích</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-red-500/10 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Bez extra splátky</p>
          <p className="text-red-400 font-bold text-lg">{(monthsWithMin / 12).toFixed(1)} let</p>
        </div>
        <div className="bg-green/10 rounded-xl p-4">
          <p className="text-gray-400 text-xs">S extra splátkou</p>
          <p className="text-green font-bold text-lg">{(monthsWithExtra / 12).toFixed(1)} let</p>
        </div>
      </div>

      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical">
            <XAxis 
              type="number"
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={110}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="months" radius={[0, 6, 6, 0]}>
              <Cell fill="#EF4444" />
              <Cell fill="#22C55E" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {monthsSaved > 0 && (
        <div className="bg-green/10 rounded-xl p-4">
          <p className="text-green text-sm">
            Budete bez dluhů o <span className="font-bold">{(monthsSaved / 12).toFixed(1)} let dříve</span> ({monthsSaved} měsíců)
          </p>
        </div>
      )}
    </div>
  );

  return (
    <CalculatorCard
      id="dluhy"
      title="Kalkulačka doplacení dluhů"
      subtitle="Snowball metoda - jak se zbavit dluhů rychleji"
      icon={Snowflake}
      tip="Metoda sněhové koule: splácejte minimum na všechny dluhy a vše navíc házejte na ten nejmenší. Až ho splatíte, přidejte tu splátku k dalšímu. Psychologický efekt malých vítězství vás udrží motivované."
      result={result}
      showResult={true}
      testId="debt-payoff-calculator"
    >
      <SliderInput
        label="Celkový dluh"
        value={totalDebt}
        onChange={setTotalDebt}
        min={10000}
        max={1000000}
        step={10000}
        suffix="Kč"
        testId="debt-total-slider"
      />
      <SliderInput
        label="Průměrná úroková sazba"
        value={interestRate}
        onChange={setInterestRate}
        min={5}
        max={30}
        step={0.5}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="debt-rate-slider"
      />
      <SliderInput
        label="Minimální měsíční splátka"
        value={minimumPayment}
        onChange={setMinimumPayment}
        min={1000}
        max={30000}
        step={500}
        suffix="Kč"
        testId="debt-minimum-slider"
      />
      <SliderInput
        label="Extra splátka navíc"
        value={extraPayment}
        onChange={setExtraPayment}
        min={0}
        max={20000}
        step={500}
        suffix="Kč"
        testId="debt-extra-slider"
      />
    </CalculatorCard>
  );
};
